import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import Snackbar from '@mui/material/Snackbar';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import { styled } from '@mui/material/styles';
import Alert from '@mui/material/Alert';
import DeleteIcon from '@mui/icons-material/Delete';
import LinearProgress from '@mui/material/LinearProgress';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

import { getManifestSettings, postManifestSettings, resetManifestSettingsState } from './manifestSettingsSlice';

// Global enterprise styling dictionary configuring standard outlined layout inputs
const enterpriseManifestInputStyles = {
    width: '100%',
    '& .MuiOutlinedInput-root': {
        borderRadius: '10px',
        backgroundColor: '#FFFFFF',
        transition: 'all 0.2s ease-in-out',
        border: '1px solid #E2E8F0',
        '& fieldset': { border: 'none' },
        '&:hover': {
            borderColor: '#CBD5E1',
            boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
        },
        '&.Mui-focused': {
            borderColor: '#1976d2', // --- CORPORATE IDENTITY BRAND BLUE ---
            boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.08)'
        }
    },
    '& .MuiInputLabel-outlined': {
        color: '#64748B',
        fontWeight: 500,
        transform: 'translate(14px, 10px) scale(1)',
        '&.Mui-focused, &.MuiInputLabel-shrink': {
            transform: 'translate(14px, -8px) scale(0.75)',
            color: '#1976d2',
            fontWeight: 600
        }
    },
    '& input, & .MuiSelect-select': {
        padding: '10px 14px !important',
        fontSize: '13.5px',
        color: '#1E293B',
        fontWeight: 500
    }
};

const ManifestSettingsPage = () => {
    const dispatch = useDispatch();
    const manifestsettings = useSelector(state => state.manifestsettings);
    const profile = useSelector(state => state.auth.profile);    
    const [alert, setAlert] = useState(false);
    const [open, setOpen] = useState(false);
    const [manifestClose, setManifestClose] = useState("manually");
    const [manifestCloseAt, setManifestCloseAt] = useState(dayjs("2023-01-01 18:00"));

    const onInputChange = e => {
        setManifestClose(e.target.value)
    }

    const z = (n) => {
        return ('0' + n).slice(-2);
      }

    const onSubmit = () => {
        const d = new Date(manifestCloseAt);
        let localTime = z(d.getHours()) + ':' + z(d.getMinutes());
        let utcTime = z(d.getUTCHours()) + ':' + z(d.getUTCMinutes());
        let data = {
            "manifest_close": manifestClose,
            "manifest_close_at": localTime,
            "manifest_close_at_utc": utcTime
        }
        dispatch(postManifestSettings(profile && profile.id,data))
    }

    useEffect(() => {
        if (manifestsettings && manifestsettings.loading === false) {
            if (manifestsettings && manifestsettings.data && manifestsettings.data.manifest_settings && manifestsettings.data.manifest_settings && manifestsettings.data.manifest_settings.automatic_close) {
                setManifestClose("automatically")
                if (manifestsettings && manifestsettings.data && manifestsettings.data.manifest_settings && manifestsettings.data.manifest_settings && manifestsettings.data.manifest_settings.close_at) {
                    setManifestCloseAt(dayjs("2023-01-01 " + manifestsettings.data.manifest_settings.close_at))
                }else{
                    setManifestCloseAt(dayjs("2023-01-01 18:00"))
                }
            } else {
                setManifestClose("manually")
                if (manifestsettings && manifestsettings.data && manifestsettings.data.manifest_settings && manifestsettings.data.manifest_settings && manifestsettings.data.manifest_settings.close_on_create_shipment) {
                    setManifestClose("oncreate")
                }
                setManifestCloseAt(dayjs("2023-01-01 18:00"))
            }
            if (manifestsettings && manifestsettings.message) {
                setAlert(true)
                setOpen(true)
            }
        }
    }, [manifestsettings]);

    const handleClose = () => {
        setOpen(false)
        dispatch(resetManifestSettingsState())
    };

    useEffect(() => {
        dispatch(getManifestSettings())
    }, [])

    return (
        <Box className="sc--settings--card" sx={{ width: '100%', boxSizing: 'border-box', py: 1 }}>

            {manifestsettings && manifestsettings.loading === false && alert &&
                <Snackbar
                    open={open}
                    anchorOrigin={{
                        vertical: "top",
                        horizontal: "right"
                    }}
                    autoHideDuration={6000}
                    onClose={handleClose}
                    sx={{ mt: 2 }}
                >
                    <Alert
                        severity={manifestsettings.hasErrors == true ? "error" : "success"}
                        onClose={handleClose}
                        variant="filled"
                        sx={{ 
                            width: '100%', 
                            borderRadius: '12px',
                            fontWeight: 600,
                            boxShadow: '0 10px 15px -3px rgba(0,0,0,0.05)',
                            fontSize: '13px',
                            alignItems: 'center'
                        }}
                    >
                        {manifestsettings && manifestsettings.message}
                    </Alert>
                </Snackbar>
            }

            <Card 
                sx={{ 
                    width: '100%',
                    maxWidth: 540,
                    mx: 'auto',
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #E2E8F0',
                    borderRadius: '16px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.02)',
                    overflow: 'hidden'
                }} 
                className="sc--cardhead--details"
            >
                <CardHeader 
                    title={
                        <Typography variant="h6" component="h1" sx={{ fontWeight: 800, color: '#0F172A', fontSize: '16px', m: 0 }}>
                            Manifest Settings
                        </Typography>
                    } 
                    sx={{ p: 2.5, backgroundColor: '#FFFFFF' }}
                />
                
                {manifestsettings && manifestsettings.loading === true && (
                    <LinearProgress sx={{ height: '3px', backgroundColor: '#E2E8F0', '& .MuiLinearProgress-bar': { backgroundColor: '#1976d2' } }} />
                )}
                
                <Divider sx={{ borderColor: '#E2E8F0' }} />
                
                {manifestsettings && manifestsettings.loading === false && (
                    <CardContent sx={{ p: 4, boxSizing: 'border-box' }}>
                        <Stack spacing={3}>
                            
                            {/* 1. Close Manifest Execution Mode Dropdown Module Field */}
                            <Box sx={{ width: '100%', minHeight: '44px' }}>
                                <FormControl variant="outlined" size="small" sx={enterpriseManifestInputStyles}>
                                    <InputLabel id="manifest-close-select-outlined-label">Close Manifest</InputLabel>
                                    <Select
                                        labelId="manifest-close-select-outlined-label"
                                        id="manifest-close-select-outlined"
                                        name="label"
                                        value={manifestClose}
                                        onChange={onInputChange}
                                        label="Close Manifest"
                                    >
                                        <MenuItem value={"manually"}>Manual</MenuItem>
                                        <MenuItem value={"automatically"}>Automatic</MenuItem>
                                        <MenuItem value={"oncreate"}>On Shipment Create</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>
                            
                            {/* 2. Asynchronous Automatic Closing Schedule Input Module Field */}
                            {manifestClose && manifestClose === 'automatically' && (
                                <Box sx={{ width: '100%', minHeight: '44px' }}>
                                    <FormControl variant="outlined" size="small" sx={{ width: '100%' }}>
                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <TimePicker
                                                label="Close at"
                                                value={manifestCloseAt}
                                                onChange={(newValue) => {
                                                    setManifestCloseAt(newValue);
                                                }}
                                                // UNCHANGED: Maintained exact existing renderInput syntax signature as requested
                                                renderInput={(params) => (
                                                    <TextField 
                                                        {...params} 
                                                        variant="outlined" 
                                                        size="small" 
                                                        sx={enterpriseManifestInputStyles} 
                                                    />
                                                )}
                                            />
                                        </LocalizationProvider>
                                    </FormControl>
                                </Box>
                            )}
                            
                            {/* 3. Processing Terminal Execution Panel Trigger Control Footer */}
                            <Box className="sc--form--center" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', pt: 1, borderTop: '1px solid #E2E8F0' }}>
                                <Button 
                                    variant="contained" 
                                    onClick={onSubmit}
                                    sx={{
                                        textTransform: 'none',
                                        borderRadius: '10px',
                                        fontWeight: 700,
                                        fontSize: '13.5px',
                                        height: '38px',
                                        px: 4,
                                        boxShadow: 'none',
                                        backgroundColor: '#1976d2',
                                        color: '#FFFFFF',
                                        '&:hover': { backgroundColor: '#115293', boxShadow: 'none' }
                                    }}
                                >
                                    Save Settings
                                </Button>
                            </Box>
                            
                        </Stack>
                    </CardContent>
                )}
            </Card >
        </Box>
    )
}

export default ManifestSettingsPage;